import { Injectable, inject } from '@angular/core';
import { FikoYTIstekKok } from '@/src/service/modules/fikoyt-istek-kok/fikoyt.istek.makinesi.service';
import { CanliGuncellemeService } from './canli-guncelleme.service';

/**
 * 🚀 PERFORMANS: Bir kartın "yorumSayisi" ve "_hesap.*" özet alanları eskiden
 * `paylasim-sistemi.component.ts`'deki feed sorgusunda HER sayfa yüklemesinde,
 * HER kart için 4 ayrı iç içe $lookup ile canlı hesaplanıyordu (bkz. performans
 * raporu). Bu servis aynı hesaplamayı SADECE bir alt kart eklendiğinde/
 * güncellendiğinde, TEK bir karta scoped şekilde çalıştırıp sonucu üst kartın
 * `_hesap` alanına kalıcı olarak yazar (denormalize/cache). Feed artık bu
 * alanları doğrudan okur, yeniden hesaplamaz.
 */
@Injectable({ providedIn: 'root' })
export class AltKartOzetService {
  private canli = inject(CanliGuncellemeService);

  /** parentId'nin _hesap özetini (paylasim-sistemi.component.ts'deki pipeline ile
   *  birebir aynı mantık, tek karta scoped) yeniden hesaplayıp üst karta yazar. */
  async ozetGuncelle(parentId: string): Promise<void> {
    if (!parentId) return;
    try {
      const aggIstek = new FikoYTIstekKok({
        metod: 'POST',
        komut: 'FikoYT.sistem.DB.hareketler.aggregate',
        parametreler: { aggregate: this._pipelineOlustur(parentId) },
      });
      const cevap: any = await aggIstek.isteSync();
      const hesap = cevap?.cevap?.sonuc?.[0];
      if (!hesap) return;

      const govde = {
        _id: parentId,
        yorumSayisi: hesap.yorumSayisi ?? 0,
        _hesap: hesap._hesap ?? {},
      };
      const formData = new FormData();
      formData.append('req_body', JSON.stringify(govde));
      const yazIstek = new FikoYTIstekKok({
        metod: 'POST',
        komut: 'FikoYT.sistem.DB.hareketler.ekle',
        parametreler: formData,
        onEkOlsun: true,
        depoModu: false,
      });
      await yazIstek.isteSync();

      // Açık sekmelerde/oturumda anlık yansısın — sunucu zaten diğer kullanıcılara socket ile yayacak
      this.canli.guncellemeYayin(parentId, govde);
    } catch (e) {
      // Sessiz — bir sonraki alt kart olayında veya manuel yenilemede telafi olur
      console.warn('[AltKartOzet] Özet güncellenemedi:', parentId, e);
    }
  }

  /** paylasim-sistemi.component.ts'deki feed pipeline'ının BİREBİR aynısı — tek farkla:
   *  $match ilk aşamada tüm listeyi değil, sadece parentId'yi hedefler. */
  private _pipelineOlustur(parentId: string): any[] {
    return [
      { $match: { _id: parentId } },
      {
        $lookup: {
          from: 'hareketler',
          let: { pid: '$_id' },
          pipeline: [
            { $match: { $expr: { $eq: ['$_uid', '$$pid'] }, turu: 'KART', silindi: { $ne: true } } },
            { $count: 'sayi' }
          ],
          as: '_altKartSayim'
        }
      },
      {
        $lookup: {
          from: 'hareketler',
          let: { pid: '$_id' },
          pipeline: [
            { $match: { $expr: { $eq: ['$_uid', '$$pid'] }, turu: 'KART', silindi: { $ne: true } } },
            { $lookup: { from: 'hareketler', localField: 'veri.icerikler', foreignField: '_id', as: 'ic' } },
            { $unwind: { path: '$ic', preserveNullAndEmptyArrays: false } },
            { $match: { 'ic.ek._secilenSetId': 'cam.kutuk.durum' } },
            { $sort: { _id: -1 } },
            { $limit: 1 }
          ],
          as: '_kutukDurumAlt'
        }
      },
      {
        $lookup: {
          from: 'hareketler',
          let: { pid: '$_id' },
          pipeline: [
            { $match: { $expr: { $eq: ['$_uid', '$$pid'] }, silindi: { $ne: true } } },
            { $lookup: { from: 'hareketler', localField: 'veri.icerikler', foreignField: '_id', as: 'ic' } },
            { $unwind: { path: '$ic', preserveNullAndEmptyArrays: false } },
            { $match: { 'ic.ek._secilenSetId': 'kutuk-stok.hareket' } },
            { $sort: { _id: 1 } },
            { $project: {
                _id: '$ic._id',
                tip: { $ifNull: ['$ic.ek.tip', { $let: { vars: { p: { $arrayElemAt: [ { $filter: { input: { $ifNull: ['$ic.ek.parametreler', []] }, as: 'x', cond: { $eq: ['$$x.ismi', 'tip'] } } }, 0 ] } }, in: { $ifNull: ['$$p.degeri', 'giris'] } } } ] },
                miktar: { $toDouble: { $ifNull: ['$ic.ek.miktar', { $let: { vars: { p: { $arrayElemAt: [ { $filter: { input: { $ifNull: ['$ic.ek.parametreler', []] }, as: 'x', cond: { $eq: ['$$x.ismi', 'miktar'] } } }, 0 ] } }, in: { $ifNull: ['$$p.degeri', 0] } } } ] } },
                aciklama: { $ifNull: ['$ic.ek.aciklama', { $let: { vars: { p: { $arrayElemAt: [ { $filter: { input: { $ifNull: ['$ic.ek.parametreler', []] }, as: 'x', cond: { $eq: ['$$x.ismi', 'aciklama'] } } }, 0 ] } }, in: { $ifNull: ['$$p.degeri', ''] } } } ] },
                ek: '$ic.ek',
            } },
          ],
          as: '_kutukStokHareket'
        }
      },
      {
        $lookup: {
          from: 'hareketler',
          let: { pid: '$_id' },
          pipeline: [
            { $match: { $expr: { $eq: ['$_uid', '$$pid'] }, turu: 'KART', silindi: { $ne: true } } },
            { $lookup: { from: 'hareketler', localField: 'veri.icerikler', foreignField: '_id', as: 'ic' } },
            { $unwind: { path: '$ic', preserveNullAndEmptyArrays: false } },
            { $match: { 'ic.ek._secilenSetId': 'proje.tanimi.durum' } },
            { $sort: { _id: -1 } },
            { $limit: 1 }
          ],
          as: '_projeDurumAlt'
        }
      },
      {
        $lookup: {
          from: 'hareketler',
          let: { pid: '$_id' },
          pipeline: [
            { $match: { $expr: { $eq: ['$_uid', '$$pid'] }, turu: 'KART', silindi: { $ne: true } } },
            { $lookup: { from: 'hareketler', localField: 'veri.icerikler', foreignField: '_id', as: 'ic' } },
            { $unwind: { path: '$ic', preserveNullAndEmptyArrays: false } },
            { $match: { 'ic.ek._secilenSetId': 'proje.tanimi.durum' } },
            { $sort: { _id: 1 } },
            { $addFields: {
                _ilerlemeTipi: { $ifNull: ['$ic.ek.ilerlemeTipi', 'kesin'] },
                _ilerlemeDeger: {
                  $cond: [
                    { $and: [{ $ne: ['$ic.ek.ilerlemeDegeri', null] }, { $ne: ['$ic.ek.ilerlemeDegeri', ''] }] },
                    { $convert: { input: '$ic.ek.ilerlemeDegeri', to: 'double', onError: 0, onNull: 0 } },
                    { $switch: { branches: [
                      { case: { $eq: ['$ic.ek.durum', 'Tamamlandı'] }, then: 100 },
                      { case: { $eq: ['$ic.ek.durum', 'Devam Ediyor'] }, then: 50 },
                      { case: { $eq: ['$ic.ek.durum', 'Beklemede'] }, then: 25 },
                      { case: { $eq: ['$ic.ek.durum', 'Planlandı'] }, then: 10 },
                      { case: { $eq: ['$ic.ek.durum', 'İptal'] }, then: 0 },
                    ], default: 0 } }
                  ]
                }
            } },
            { $group: { _id: null, kayitlar: { $push: { tipi: '$_ilerlemeTipi', deger: '$_ilerlemeDeger', durum: '$ic.ek.durum' } } } },
            { $addFields: {
                hamIlerleme: {
                  $reduce: {
                    input: '$kayitlar', initialValue: 0,
                    in: { $cond: [{ $eq: ['$$this.tipi', 'eklemeli'] }, { $add: ['$$value', '$$this.deger'] }, '$$this.deger'] }
                  }
                },
                toplam: { $size: '$kayitlar' },
                tamamlanan: {
                  $size: { $filter: { input: '$kayitlar', cond: { $or: [
                    { $eq: ['$$this.durum', 'Tamamlandı'] }, { $gte: ['$$this.deger', 100] }
                  ] } } }
                }
            } },
            { $addFields: { ilerleme: { $max: [0, { $min: [100, '$hamIlerleme'] }] } } }
          ],
          as: '_projeIlerlemeAlt'
        }
      },
      {
        $addFields: {
          yorumSayisi: { $ifNull: [{ $arrayElemAt: ['$_altKartSayim.sayi', 0] }, 0] },
          '_hesap.altKartSayisi': { $ifNull: [{ $arrayElemAt: ['$_altKartSayim.sayi', 0] }, 0] },
          '_hesap.sonDurum': { $ifNull: [{ $arrayElemAt: ['$_kutukDurumAlt.ic.ek.durum', 0] }, null] },
          '_hesap.kalanStok': {
            $reduce: {
              input: '$_kutukStokHareket',
              initialValue: 0,
              in: { $switch: { branches: [
                { case: { $eq: ['$$this.tip', 'giris'] }, then: { $add: ['$$value', '$$this.miktar'] } },
                { case: { $in: ['$$this.tip', ['cikis', 'fire']] }, then: { $subtract: ['$$value', '$$this.miktar'] } },
                { case: { $eq: ['$$this.tip', 'sayim'] }, then: '$$value' },
              ], default: '$$value' } },
            },
          },
          '_hesap.altHareketler': { $slice: ['$_kutukStokHareket', -5] },
          '_hesap.projeSonDurum': { $ifNull: [{ $arrayElemAt: ['$_projeDurumAlt.ic.ek.durum', 0] }, null] },
          '_hesap.projeIlerleme': { $round: [{ $ifNull: [{ $arrayElemAt: ['$_projeIlerlemeAlt.ilerleme', 0] }, 0] }, 0] },
          '_hesap.projeAltToplam': { $ifNull: [{ $arrayElemAt: ['$_projeIlerlemeAlt.toplam', 0] }, 0] },
          '_hesap.projeAltTamamlanan': { $ifNull: [{ $arrayElemAt: ['$_projeIlerlemeAlt.tamamlanan', 0] }, 0] },
          '_hesap.olculer': {
            $let: {
              vars: { ic: { $arrayElemAt: ['$veri.icerikler', 0] } },
              in: {
                $sortArray: {
                  input: {
                    $filter: {
                      input: [
                        { $convert: { input: '$$ic.ek.en', to: 'double', onError: null, onNull: null } },
                        { $convert: { input: '$$ic.ek.boy', to: 'double', onError: null, onNull: null } },
                        { $convert: { input: '$$ic.ek.yukseklik', to: 'double', onError: null, onNull: null } }
                      ],
                      as: 'v',
                      cond: { $and: [{ $ne: ['$$v', null] }, { $gt: ['$$v', 0] }] }
                    }
                  },
                  sortBy: 1
                }
              }
            }
          }
        }
      },
      {
        $addFields: {
          '_hesap.enKucukOlcu': { $arrayElemAt: ['$_hesap.olculer', 0] },
          '_hesap.ortancaOlcu': { $arrayElemAt: ['$_hesap.olculer', 1] },
          '_hesap.enBuyukOlcu': { $arrayElemAt: ['$_hesap.olculer', 2] },
        }
      },
      { $project: { yorumSayisi: 1, _hesap: 1 } },
    ];
  }
}
